import React, { useEffect, useMemo, useRef, useState } from 'react';
import '@pages/Assets/Assets.less';

import { Navigate, useParams } from 'react-router-dom';
import {
  AssetsList as InternalAssetsList,
  FoldersList
} from '@components/Assets';
import { useCurrentWorkspace, useDefaultWorkspace } from '@hooks/workspace';
import PageSortPanel from '@components/PageSortPanel';
import { AssetControllerListAssetsParams } from '@api/Api';
import { toggleUploadAssetModal } from '@redux/actions/modalAction';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import { Menu } from 'antd';
import { useWebSocketDebouncedSubscription } from '@hooks/useWebSocketClient';

import { useLayout } from '@layouts/PageLayout';
import { ReactComponent as DeletedSvg } from '@assets/icons/deleted-tasks-head-icon.svg';
import { useAssetsCountQuery, useAssetsQuery } from '@hooks/queries/assets';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { useDebounced } from '@helpers/useDebounced';
import { useQueryClient } from '@tanstack/react-query';
import StatusesFilter from '@components/Approval/StatusesFilter/StatusesFilter';
import CampaignsFilter from '@components/Approval/CampaignsFilter/CampaignsFilter';

type AssetsOrderBy = NonNullable<
  AssetControllerListAssetsParams['orderBy']
>[number];

function modifyParams(
  page: 'all' | 'my' | 'favorite' | 'deleted',
  workspaceId: string
) {
  let params: AssetControllerListAssetsParams = {
    workspaceId
  };
  if (page === 'my')
    params = {
      ...params,
      own: true
    };

  if (page === 'favorite')
    params = {
      ...params,
      isFavorite: true
    };
  if (page === 'deleted')
    params = {
      ...params,
      isDeleted: true
    };

  return params;
}

function AssetsInWorkspace({
  page
}: {
  page: 'all' | 'my' | 'favorite' | 'deleted';
}) {
  const { currentView } = useLayout();
  const dispatch = useTypedDispatch();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentWorkspace] = useCurrentWorkspace(true);
  const [orderBy, setOrderBy] = useState<AssetsOrderBy>('order:DESC');
  const [campaignId, setCampaignId] = useState<string>();
  const [awStatuses, setAwStatuses] = useState<any[]>([]);
  const debouncedSearchQuery = useDebounced(searchQuery, 250);

  const prevPage = useRef(page);

  const queryClient = useQueryClient();
  const assetsQuery = useAssetsQuery(
    {
      ...modifyParams(page, currentWorkspace.id),
      flatten: true,
      isFolder: false,
      searchQuery: debouncedSearchQuery,
      awStatuses,
      campaignId,
      orderBy: [orderBy]
    },
    { keepPreviousData: prevPage.current === page }
  );
  const assetsCountQuery = useAssetsCountQuery(
    {
      ...modifyParams(page, currentWorkspace.id),
      flatten: true,
      isFolder: false,
      searchQuery: debouncedSearchQuery,
      awStatuses,
      campaignId
    },
    { placeholderData: 25, keepPreviousData: prevPage.current === page }
  );

  const foldersQuery = useAssetsQuery(
    {
      ...modifyParams(page, currentWorkspace.id),
      flatten: true,
      isFolder: true,
      searchQuery: debouncedSearchQuery,
      orderBy: [orderBy]
    },
    { keepPreviousData: prevPage.current === page }
  );
  const foldersCountQuery = useAssetsCountQuery(
    {
      ...modifyParams(page, currentWorkspace.id),
      flatten: true,
      isFolder: true,
      searchQuery: debouncedSearchQuery
    },
    { enabled: page === 'deleted', keepPreviousData: prevPage.current === page }
  );

  if (page !== prevPage.current) {
    prevPage.current = page;
  }

  const assetsList = useMemo(() => {
    return assetsQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [assetsQuery.data]);

  const foldersList = useMemo(() => {
    return foldersQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [foldersQuery.data]);

  const assetsCount = assetsCountQuery.data ?? 0;
  const foldersCount = foldersCountQuery.data ?? 0;
  const assetsToFetch = Math.max(0, assetsCount - assetsList.length);
  const foldersToFetch = Math.max(0, foldersCount - foldersList.length);

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== currentWorkspace.id) return false;
      if (msg.payload.reason === 'moved-to-folder') return false;
      if (msg.causedByMe && msg.payload.reason !== 'upload') return false;
      return true;
    },
    'asset-created'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== currentWorkspace.id) return false;
      if (msg.payload.reason === 'moved-to-folder') return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'asset-removed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== currentWorkspace.id) return false;
      if (msg.causedByMe && msg.payload.reason !== 'conversion-status-changed')
        return false;
      return true;
    },
    'asset-updated'
  );

  return (
    <div>
      <div className="inner-header">
        <div className="inner-header__name">
          {page === 'deleted' && <DeletedSvg className="inner-header__icon" />}
          <span className="inner-header__title">
            {page.toUpperCase()} MEDIA
          </span>
          <div className="inner-header__count">
            {page === 'deleted' ? assetsCount + foldersCount : assetsCount}
          </div>
        </div>
        {page === 'deleted' && (
          <span className="inner-header__description main-body-text main-body-text--dark-gray">
            Creative media will be permanently deleted after{' '}
            <span className="main-body-text--main-color">30&nbsp;days</span>
          </span>
        )}
      </div>
      <div className="sort-panel-container">
        <div className="filter-panels-container">
          <CampaignsFilter
            onChange={(ids) =>
              setCampaignId((prev) => ids.find((id) => id !== prev) ?? ids[0])
            }
            selectedCampaignIds={campaignId ? [campaignId] : []}
          />
          <StatusesFilter
            onChange={setAwStatuses}
            selectedStatuses={awStatuses}
          />
        </div>
        <PageSortPanel
          entity="media"
          actionButtonName="Upload new media"
          onAddClick={
            page === 'all'
              ? () =>
                  dispatch(
                    toggleUploadAssetModal({
                      visible: true,
                      workspaceId: currentWorkspace.id,
                      campaignId
                    })
                  )
              : undefined
          }
          onSearch={setSearchQuery}
          sortMenuModal={
            <Menu
              selectedKeys={[orderBy]}
              onClick={async ({ key }) => setOrderBy(key as AssetsOrderBy)}
            >
              <span className="help_dropdown__title">Sort by</span>
              <Menu.Item key="createdAt:DESC">
                <span className="help_dropdown__dot" /> Date created
              </Menu.Item>
              <Menu.Item key="modifiedAt:DESC">
                <span className="help_dropdown__dot" /> Date modified
              </Menu.Item>
            </Menu>
          }
          onOrderBy={undefined}
          classNameSearchContainer={undefined}
          classNameActionButton={undefined}
          searchInputComponent={undefined}
          classNamePanel={undefined}
        />
      </div>
      <div
        style={{
          padding: '0 34px',
          margin: '32px -34px 0',
          height: 'calc(100vh - 288px)',
          overflow: 'auto'
        }}
      >
        {!!foldersCount && (
          <>
            <InfiniteScroll
              loading={foldersQuery.isFetchingNextPage ? 'more' : false}
              next={foldersQuery.fetchNextPage}
              hasMore={foldersQuery.hasNextPage || false}
            >
              <div className="campaign_items__title">
                {page === 'deleted' ? 'DELETED FOLDERS' : 'FOLDERS'}
              </div>
              <FoldersList
                foldersList={foldersList}
                countToFetch={
                  foldersQuery.isLoading || foldersQuery.isFetchingNextPage
                    ? foldersToFetch
                    : 0
                }
                isCardViewMode={currentView === 'tile'}
                listLoading={foldersQuery.isLoading}
              />
            </InfiniteScroll>
            <div className="campaign_items__title">
              {page === 'deleted' ? 'DELETED MEDIA' : 'MEDIA'}
            </div>
          </>
        )}

        <InfiniteScroll
          loading={assetsQuery.isFetchingNextPage ? 'more' : false}
          next={assetsQuery.fetchNextPage}
          hasMore={assetsQuery.hasNextPage || false}
        >
          <InternalAssetsList
            searchQuery={searchQuery}
            assetsList={assetsList}
            isTileView={currentView === 'tile'}
            displayAssetsCount={assetsCount}
            page={page}
            countToFetch={assetsQuery.isFetchingNextPage ? assetsToFetch : 0}
            listLoading={assetsQuery.isLoading}
          />
        </InfiniteScroll>
      </div>
    </div>
  );
}

function AssetsList(props: { page: 'all' | 'my' | 'favorite' | 'deleted' }) {
  const { workspaceId: workspaceIdParam } = useParams();
  const defaultWorkspace = useDefaultWorkspace(false);
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);
  const workspaceId =
    workspaceIdParam ?? currentWorkspace?.id ?? defaultWorkspace?.id;
  useEffect(() => {
    if (workspaceId) setCurrentWorkspace(workspaceId);
  }, [workspaceId, setCurrentWorkspace]);

  const needRedirect = !workspaceId || workspaceId !== workspaceIdParam;
  if (needRedirect) {
    return <Navigate to={workspaceId ?? '/dashboard'} replace />;
  }
  return currentWorkspace ? (
    <AssetsInWorkspace
      key={`${currentWorkspace.id}_${props.page}`}
      {...props}
    />
  ) : null;
}

export default AssetsList;
